import React, { useState, useMemo, useEffect, useRef } from "react";
import styles from "./TableAccordion.module.scss";
import { ReactComponent as DownArrowIcon } from "../../icons/down-arrow.svg";
import { ReactComponent as UpArrowIcon } from "../../icons/up-arrow.svg";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import Table from "../Table/Table";
import {
  formatDate,
  generateCumulativeTimeSeries,
  getPast12Months,
} from "../../utils/helpers";
import { useDispatch, useSelector } from "react-redux";
import TableSelect from "../TableSelect/TableSelect";
import Input from "../Input/Input";
import TableInput from "../TableInput/TableInput";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import { getOrganizationData } from "../../actions/organization";
import axiosInstance from "../../utils/utils";

const TableAccordion = () => {
  const { orgId } = useParams();
  const sessionToken = localStorage.getItem("session_token");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [openChapters, setOpenChapters] = useState([]);
  const [editedRisks, setEditedRisks] = useState({});
  const [editedLevels, setEditedLevels] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingRisks, setLoadingRisks] = useState({});
  const { findingsData, improvementsData, risksData } = useSelector(
    (state) => state.organization,
  );
  const currentUser = useSelector((state) => state.user.user);

  const canEdit = useMemo(() => {
    const role = currentUser?.Role?.toLowerCase?.();
    return role === "consultant" || role === "qmsadmin";
  }, [currentUser]);

  const last12MonthsFindings = useMemo(() => {
    const months = getPast12Months();
    const [startY, startM] = months[0].split("-").map(Number);
    const [endY, endM] = months[11].split("-").map(Number);
    const start = new Date(startY, startM - 1, 1);
    // first day of the month after the last window month (exclusive upper bound)
    const end = new Date(endY, endM, 1);

    return Object.values(findingsData || {})
      .filter((f) => !f.archived)
      .map((finding) => {
        const updatedRisk = risksData?.[finding?.risk?.risk_num];
        return updatedRisk
          ? { ...finding, risk: updatedRisk } // override with latest risk data to recalculate rps when level changes
          : finding;
      })
      .filter((finding) => {
        if (!finding.date) return false;
        const [y, m, d] = finding.date.split("-").map(Number);
        if (!y || !m || !d) return false;
        const date = new Date(y, m - 1, d);
        return date >= start && date < end;
      });
  }, [findingsData, risksData]);

  const groupedTimeSeries = useMemo(() => {
    const findingsMap = Object.fromEntries(
      last12MonthsFindings
        .filter((f) => {
          const riskLevel = f?.risk?.level?.toLowerCase();
          return riskLevel && riskLevel !== "none";
        })
        .map((f) => [f.key, f]),
    );

    const improvementsMap = Object.fromEntries(
      Object.values(improvementsData || {})
        .filter((i) => !i.archived)
        .map((i) => [i.key, i]),
    );

    return generateCumulativeTimeSeries(findingsMap, improvementsMap);
  }, [last12MonthsFindings, improvementsData]);

  useEffect(() => {
    // Check if any loading risks are now resolved
    Object.entries(loadingRisks).forEach(([riskNum, isLoading]) => {
      if (!isLoading) return;

      const updatedLevel = risksData?.[riskNum]?.level;
      const expectedLevel = editedLevels[riskNum];
      if (
        expectedLevel &&
        updatedLevel &&
        expectedLevel.toLowerCase() === updatedLevel.toLowerCase()
      ) {
        setLoadingRisks((prev) => ({ ...prev, [riskNum]: false }));
      }
    });
  }, [risksData, loadingRisks, editedLevels]);

  const groupedChapters = useMemo(() => {
    const grouped = {};

    Object.values(risksData || {}).forEach((risk) => {
      const mainChapterKey = risk.qms_id?.[0];
      if (!mainChapterKey) return;

      if (!grouped[mainChapterKey]) grouped[mainChapterKey] = [];
      grouped[mainChapterKey].push(risk);
    });

    return grouped;
  }, [risksData]);

  const sortedChapterKeys = useMemo(() => {
    return Object.keys(groupedChapters).sort((a, b) => a.localeCompare(b));
  }, [groupedChapters]);

  const toggleChapter = (key) => {
    setOpenChapters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const getPermissionLabel = (role) => {
    if (!role) return "";

    const normalizedRole = role.toLowerCase();
    if (normalizedRole === "consultant" || normalizedRole === "qmsadmin") {
      return "can edit";
    }
    if (normalizedRole === "qmsmanager" || normalizedRole === "qmsuser") {
      return "can view";
    }
    return "admin"; // fallback
  };

  const handleEdit = async (riskId, field, value) => {
    if (field === "level") {
      setLoadingRisks((prev) => ({ ...prev, [riskId]: true }));
    }

    const existingRisk = risksData?.[riskId];

    // Get latest values: fallback -> edited -> original
    const currentReason =
      field === "reason"
        ? value
        : (editedRisks[riskId]?.reason ?? existingRisk?.reason ?? "");

    const currentLevel =
      field === "level"
        ? value
        : (editedLevels[riskId] ?? existingRisk?.level ?? "none");

    // Update local state
    if (field === "reason") {
      setEditedRisks((prev) => {
        const prevEntry = prev[riskId] || {};
        if (prevEntry[field] === value) return prev;
        return {
          ...prev,
          [riskId]: {
            ...prevEntry,
            [field]: value,
          },
        };
      });
    } else if (field === "level") {
      setEditedLevels((prev) => {
        if (prev[riskId] === value) return prev;
        return {
          ...prev,
          [riskId]: value,
        };
      });
    }

    // Always send both values in the PATCH
    const payload = [
      {
        risk_num: riskId,
        reason: currentReason,
        level: currentLevel,
      },
    ];

    try {
      await axiosInstance.patch(
        `/nocodb/risk_profile?org_id=${orgId}`,
        payload,
        {
          headers: {
            "session-token": sessionToken,
            "Content-Type": "application/json",
          },
        },
      );

      dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          false,
          false,
          false,
          false,
        ),
      );
    } catch (err) {
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "error",
        title: "Failed to update risk profile",
        text: "Please try again.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    }
  };

  const getExposureByRisk = useMemo(() => {
    const exposure = {};

    last12MonthsFindings.forEach((finding) => {
      const riskNum = finding?.risk?.risk_num;
      if (!riskNum) return;

      const risk = risksData?.[riskNum];
      const level = risk?.level?.toLowerCase();

      if (!level || level === "none") return;

      const impact =
        level === "low" ? 1 : level === "medium" ? 2 : level === "high" ? 3 : 0;

      exposure[riskNum] = (exposure[riskNum] || 0) + impact;
    });

    return exposure;
  }, [last12MonthsFindings, risksData]);

  const priorityScoreByRisk = useMemo(() => {
    const priority = {};
    Object.entries(groupedTimeSeries).forEach(([riskId, monthlyData]) => {
      const lastMonth = monthlyData[monthlyData.length - 1];
      priority[riskId] = lastMonth?.count || 0;
    });
    return priority;
  }, [groupedTimeSeries]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.accordionHeaderRow}>
        <div className={styles.leftControls}>
          <div
            className={styles.toggleAll}
            onClick={() => {
              const allKeys = sortedChapterKeys;
              const areAllOpen = allKeys.every((key) =>
                openChapters.includes(key),
              );
              setOpenChapters(areAllOpen ? [] : allKeys);
            }}
          >
            {openChapters.length === sortedChapterKeys.length ? (
              <UpArrowIcon className={styles.toggleAllIcon} />
            ) : (
              <DownArrowIcon className={styles.toggleAllIcon} />
            )}
            <span>ALL</span>
          </div>
          <Input
            type="text"
            placeholder="Search by description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={styles.inputTableAccordion}
            icon={SearchIcon}
          />
        </div>

        <div className={styles.rightControls}>
          <div className={styles.permissionLabel}>
            {currentUser?.Email} {getPermissionLabel(currentUser?.Role)}
          </div>
        </div>
      </div>
      {sortedChapterKeys.map((chapterKey) => {
        const risks = (groupedChapters[chapterKey] || []).filter((risk) =>
          risk.description?.toLowerCase().includes(searchQuery.toLowerCase()),
        );
        if (risks.length === 0) return null;

        const levelCounts = risks.reduce(
          (acc, r) => {
            const level = (r.level || "").toLowerCase();
            if (level && acc[level] !== undefined) acc[level]++;
            return acc;
          },
          { high: 0, medium: 0, low: 0, none: 0 },
        );

        const getCountForRisk = (risk_num, groupedData) => {
          const riskData = groupedData[risk_num];
          if (!riskData) return "No data"; // If no data exists for the risk number

          const latestMonthData = riskData[riskData.length - 1];
          return latestMonthData ? latestMonthData.count : "No data";
        };

        const columns = [
          {
            accessorKey: "risk_id",
            header: "ID",
            size: 80,
            cell: (props) => <div>{props.row.original.risk_id}</div>,
            sortingFn: (a, b) =>
              a.original.risk_id.localeCompare(b.original.risk_id, undefined, {
                numeric: true,
              }),
            enableSorting: true,
          },
          {
            accessorKey: "chapter",
            header: "Chapter",
            cell: (props) => {
              const qmsId = props.row.original?.qms_id;
              const qmsDescription = props.row.original?.qms_description;

              return (
                <div>
                  {qmsId && qmsDescription ? (
                    <div>
                      {qmsId} - {qmsDescription}
                    </div>
                  ) : (
                    "No Chapter Info"
                  )}
                </div>
              );
            },
            size: 270,
            enableSorting: false,
          },
          {
            accessorKey: "description",
            header: "DESCRIPTION",
            size: 350,
            cell: (props) => <div>{props.getValue()}</div>,
            enableSorting: false,
          },
          {
            accessorKey: "level",
            header: "LEVEL",
            size: 110,
            cell: (props) => {
              const row = props.row.original;
              const riskId = row.risk_num;

              if (!canEdit) {
                return <div>{row.level || "None"}</div>;
              }

              return (
                <TableSelect
                  value={editedLevels[riskId] ?? row.level}
                  onChange={(newValue) => handleEdit(riskId, "level", newValue)}
                  options={[
                    { id: "none", label: "None" },
                    { id: "low", label: "Low" },
                    { id: "medium", label: "Medium" },
                    { id: "high", label: "High" },
                  ]}
                  riskNum={riskId}
                />
              );
            },
            enableSorting: false,
          },
          {
            accessorKey: "exposure",
            header: "EXPOSURE",
            size: 120,
            cell: (props) => {
              const riskNum = props.row.original?.risk_num;

              if (loadingRisks[riskNum]) {
                return (
                  <div className={styles.loaderCell}>
                    <div className={styles.spinner}></div>
                  </div>
                );
              }

              return <div>{getExposureByRisk[riskNum] ?? 0}</div>;
            },
            enableSorting: false,
          },
          {
            accessorKey: "count",
            header: "PRIORITY SCORE",
            size: 180,
            cell: (props) => {
              const riskId = props.row.original?.risk_id;
              const riskNum = props.row.original?.risk_num;

              if (loadingRisks[riskNum]) {
                return (
                  <div className={styles.loaderCell}>
                    <div className={styles.spinner}></div>
                  </div>
                );
              }

              const score = priorityScoreByRisk[riskId];
              return <div>{score ?? "No data"}</div>;
            },
            enableSorting: false,
          },
          {
            accessorKey: "reason",
            header: "REASON",
            size: 250,
            cell: (props) => {
              const id = props.row.original.risk_num;
              const current = editedRisks[id]?.reason ?? props.getValue();

              return (
                <TableInput
                  id={id}
                  initialValue={current}
                  onSave={(value) => handleEdit(id, "reason", value)}
                  canEdit={canEdit}
                />
              );
            },
            enableSorting: false,
          },
          {
            accessorKey: "date",
            header: "DATE",
            size: 120,
            cell: (props) => {
              const rawDate = props.getValue();
              return <div>{rawDate ? formatDate(rawDate) : "Not defined"}</div>;
            },
            enableSorting: false,
          },
        ];

        return (
          <div key={chapterKey} className={styles.accordionItem}>
            <div
              className={styles.accordionHeader}
              onClick={() => toggleChapter(chapterKey)}
            >
              {openChapters.includes(chapterKey) ? (
                <UpArrowIcon className={styles.icon} />
              ) : (
                <DownArrowIcon className={styles.icon} />
              )}
              <div className={styles.chapterTitle}>
                Chapter {chapterKey}
                <div className={styles.levelPills}>
                  {levelCounts.high > 0 && (
                    <span className={styles.high}>{levelCounts.high} High</span>
                  )}
                  {levelCounts.medium > 0 && (
                    <span className={styles.medium}>
                      {levelCounts.medium} Medium
                    </span>
                  )}
                  {levelCounts.low > 0 && (
                    <span className={styles.low}>{levelCounts.low} Low</span>
                  )}
                  {levelCounts.none > 0 && (
                    <span className={styles.none}>
                      {levelCounts.none} Not applicable
                    </span>
                  )}
                </div>
              </div>
            </div>
            {openChapters.includes(chapterKey) && (
              <div className={styles.accordionBody}>
                <Table
                  columns={columns}
                  data={risks}
                  initialSorting={[{ id: "risk_id", desc: false }]}
                  variant="tableAccordionVariant"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TableAccordion;
